import { useEffect, useState } from "react";

import { Input, Slider, TextareaAutosize, Typography } from "@mui/material";
import CustomSelect from "../../../../components/UI/CustomSelect";

const ImageEditingSettings = ({
  onSettingsChange,
  selectedPrompt,
  generateExact,
}) => {
  const [selectedModel, setSelectedModel] = useState("baby-version");
  const [editingStrength, setEditingStrength] = useState(0.8);
  const [prompt, setPrompt] = useState(selectedPrompt?.prompt || "");
  const [seed, setSeed] = useState(generateExact ? selectedPrompt?.seed : "");
  const models = [
    {
      id: "baby-version",
      name: "Baby Version",
      description: "Simple and intuitive image editing",
    },
    {
      id: "cartoonify",
      name: "Cartoonify",
      description: "Transform images into cartoon style",
    },
    {
      id: "expression-change",
      name: "Expression Change",
      description: "Change facial expressions in images",
    },
    {
      id: "object-removal",
      name: "Object Removal",
      description: "Remove unwanted objects from images",
    },
    {
      id: "background-change",
      name: "Background Change",
      description: "Replace or modify image backgrounds",
    },
    {
      id: "hair-change",
      name: "Hair Change",
      description: "Modify hairstyles and hair color",
    },
    {
      id: "style-transfer",
      name: "Style Transfer",
      description: "Apply artistic styles to images",
    },
  ];

  const handleChange = (field, value) => {
    if (onSettingsChange) {
      onSettingsChange({ [field]: value });
    }
  };

  useEffect(() => {
    // Update settings when selectedPrompt changes
    if (selectedPrompt) {
      setPrompt(selectedPrompt?.prompt || "");
      handleChange("prompt", selectedPrompt?.prompt || "");
    } else {
      setPrompt("");
      handleChange("prompt", "");
    }
  }, [selectedPrompt?.prompt]);
  useEffect(() => {
    // Update settings when selectedPrompt changes
    if (selectedPrompt?.seed && generateExact) {
      setSeed(selectedPrompt.seed);
      handleChange("seed", selectedPrompt.seed);
    } else {
      // If generateExact is false, we can set seed to an empty string
      setSeed("");
      handleChange("seed", "");
    }
  }, [selectedPrompt?.seed, generateExact]);

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    if (onSettingsChange) {
      onSettingsChange({ model: modelId });
    }
  };

  return (
    <div className="space-y-4">
      <CustomSelect
        options={models}
        defaultValue="baby-version"
        placeholder="Select a model"
        value={selectedModel}
        onChange={(value) => handleChange("model", value)}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Typography className="text-gray-300">
            Editing Strength {editingStrength}
          </Typography>
          <Slider
            value={editingStrength}
            min={0.1}
            max={1}
            step={0.1}
            className="w-full"
            aria-label="Default"
            valueLabelDisplay="auto"
            onChange={(e) => {
              handleChange("strength", parseFloat(e.target.value));
              setEditingStrength(parseFloat(e.target.value));
            }}
          />
          <p className="text-xs text-gray-400 mt-1">
            Controls how strong the editing effect is (0.1 - 1.0)
          </p>
        </div>

        <div>
          <Typography className="text-gray-300">Guidance Scale</Typography>
          <Input
            type="number"
            min="1"
            max="20"
            defaultValue="7.5"
            step="0.5"
            className="w-full bg-gray-700 border-gray-600 text-white rounded p-2"
            onChange={(e) =>
              handleChange("guidanceScale", parseFloat(e.target.value))
            }
            inputProps={{ style: { color: "white" } }}
          />
          <p className="text-xs text-gray-400 mt-1">
            How closely to follow the prompt (1-20)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Typography className="text-gray-300">Inference Steps</Typography>
          <Input
            type="number"
            min="10"
            max="50"
            defaultValue="20"
            className="w-full bg-gray-700 border-gray-600 text-white rounded p-2"
            onChange={(e) =>
              handleChange("numInferenceSteps", parseInt(e.target.value))
            }
            inputProps={{ style: { color: "white" } }}
          />
          <p className="text-xs text-gray-400 mt-1">
            Number of denoising steps (10-50)
          </p>
        </div>

        <div>
          <Typography className="text-gray-300">Seed (Optional)</Typography>
          <Input
            type="number"
            placeholder="Random seed"
            className="w-full bg-gray-700 border-gray-600 text-white rounded p-2"
            onChange={(e) => {
              setSeed(e.target.value);
              handleChange("seed", e.target.value);
            }}
            inputProps={{ style: { color: "white" } }}
            value={seed}
          />
          <p className="text-xs text-gray-400 mt-1">
            Seed for reproducible results
          </p>
        </div>
      </div>

      <div>
        <Typography className="text-gray-300">Edit Prompt</Typography>
        <TextareaAutosize
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white resize-none"
          rows={3}
          placeholder="Describe how you want to edit the image..."
          onChange={(e) => {
            setPrompt(e.target.value);
            handleChange("prompt", e.target.value);
          }}
          value={prompt}
        />
        <p className="text-xs text-gray-400 mt-1">
          Describe the edits you want to make to the image
        </p>
      </div>

      <div>
        <Typography className="text-gray-300">Negative Prompt</Typography>
        <TextareaAutosize
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white resize-none"
          rows={2}
          placeholder="What you don't want in the result..."
          onChange={(e) => handleChange("negativePrompt", e.target.value)}
        />
        <p className="text-xs text-gray-400 mt-1">
          Describe what should be avoided
        </p>
      </div>
    </div>
  );
};

export default ImageEditingSettings;
